import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { toast } from 'sonner';
import { ArrowRight, ArrowLeft, TrendingUp, Info } from 'lucide-react';
import { adaptiveMacrosApi } from '../api/adaptiveMacros';

const PACE_OPTIONS = [
  { value: 50, label: 'Conservative', hint: '+50 kcal/week · ~12 weeks · minimal rebound risk' },
  { value: 75, label: 'Moderate', hint: '+75 kcal/week · ~8 weeks · balanced' },
  { value: 100, label: 'Aggressive', hint: '+100 kcal/week · ~5 weeks · faster to maintenance' },
];

const ReverseDietOnboarding = ({ open, onClose, onComplete, clientId = null }) => {
  const [step, setStep] = useState(1);
  const [targetMaintenance, setTargetMaintenance] = useState('');
  const [weeklyIncrease, setWeeklyIncrease] = useState(75);
  const [autoPause, setAutoPause] = useState(true);
  const [maxGain, setMaxGain] = useState('0.5');
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setStep(1);
    setTargetMaintenance('');
    setWeeklyIncrease(75);
    setAutoPause(true);
    setMaxGain('0.5');
  };

  const close = () => {
    onClose?.();
    setTimeout(reset, 200);
  };

  const canProceed1 = !!targetMaintenance && Number(targetMaintenance) >= 1200;
  const canSubmit = canProceed1 && PACE_OPTIONS.some((p) => p.value === weeklyIncrease);

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      await adaptiveMacrosApi.enable({
        goal: 'reverse',
        aggressiveness: 'moderate',
        client_id: clientId,
        reverse_config: {
          weekly_kcal_increase: Number(weeklyIncrease),
          target_maintenance_kcal: Number(targetMaintenance),
          max_weight_gain_kg: Number(maxGain) || 0.5,
          auto_pause_on_gain: !!autoPause,
          week_number: 0,
          started_at: new Date().toISOString(),
        },
      });
      toast.success('Reverse diet started');
      onComplete?.();
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start reverse diet');
    } finally {
      setSaving(false);
    }
  };

  const weeksToComplete = (() => {
    const maint = Number(targetMaintenance);
    if (!maint || !weeklyIncrease) return null;
    // Assume start at ~maint-500 (post-cut) — rough UI estimate
    return Math.max(1, Math.ceil(500 / weeklyIncrease));
  })();

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-md max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-[#FF5C00]" />
            Start Reverse Diet
          </DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            <div className="rounded-lg bg-muted/40 p-3 text-xs text-muted-foreground flex gap-2">
              <Info className="h-4 w-4 shrink-0 mt-0.5" />
              <p>A reverse diet gradually raises kcal after a cut to restore metabolism and minimize fat regain. Enter the maintenance you'd like to reach.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="target-maint">Target maintenance kcal</Label>
              <Input
                id="target-maint"
                type="number"
                inputMode="numeric"
                min={1200}
                max={6000}
                placeholder="e.g. 2400"
                value={targetMaintenance}
                onChange={(e) => setTargetMaintenance(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                This is where you stop ramping and hold. Usually your pre-cut TDEE.
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={close}>Cancel</Button>
              <Button onClick={() => setStep(2)} disabled={!canProceed1}>
                Next <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <Label>Pace</Label>
            <RadioGroup value={String(weeklyIncrease)} onValueChange={(v) => setWeeklyIncrease(Number(v))} className="grid gap-2">
              {PACE_OPTIONS.map((p) => (
                <label
                  key={p.value}
                  htmlFor={`pace-${p.value}`}
                  className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${Number(weeklyIncrease) === p.value ? 'border-primary bg-primary/5' : 'border-border'}`}
                >
                  <RadioGroupItem value={String(p.value)} id={`pace-${p.value}`} className="mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{p.label}</div>
                    <div className="text-xs text-muted-foreground">{p.hint}</div>
                  </div>
                </label>
              ))}
            </RadioGroup>
            {weeksToComplete && (
              <p className="text-xs text-muted-foreground">
                Estimate: ~{weeksToComplete} weeks to reach {targetMaintenance} kcal.
              </p>
            )}
            <div className="flex justify-between gap-2 pt-2">
              <Button variant="ghost" onClick={() => setStep(1)}>
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              <Button onClick={() => setStep(3)}>
                Next <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div className="min-w-0">
                <div className="font-medium text-sm">Auto-pause on rapid gain</div>
                <div className="text-xs text-muted-foreground">
                  Skip the weekly increase if weight climbs too fast
                </div>
              </div>
              <Switch checked={autoPause} onCheckedChange={setAutoPause} />
            </div>
            {autoPause && (
              <div className="space-y-2">
                <Label htmlFor="max-gain">Max weight gain before pause (kg/week)</Label>
                <Input
                  id="max-gain"
                  type="number"
                  step="0.1"
                  min="0.1"
                  max="2"
                  value={maxGain}
                  onChange={(e) => setMaxGain(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  0.5 kg/week is a safe default. Above this, the weekly kcal bump is skipped.
                </p>
              </div>
            )}
            <div className="flex justify-between gap-2 pt-2">
              <Button variant="ghost" onClick={() => setStep(2)}>
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              <Button
                onClick={submit}
                disabled={!canSubmit || saving}
                className="bg-[#FF5C00] hover:bg-[#FF5C00]/90 text-white shadow-[0_0_24px_rgba(255,107,47,0.35)]"
              >
                {saving ? 'Starting…' : 'Start Reverse Diet'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReverseDietOnboarding;
